'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    getPipelines,
    SalesPipeline
} from '@/lib/api/sales/pipelines-api'
import {
    createSLA,
    CreateSLAData,
    deleteSLA,
    getSLABreaches,
    getSLAs,
    resolveSLABreach,
    SalesSLA,
    SLA_TYPE_DESCRIPTIONS,
    SLA_TYPE_LABELS,
    SLABreach,
    SLABreachFilters,
    SLAType,
    updateSLA,
    UpdateSLAData
} from '@/lib/api/sales/slas-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useMemo, useState } from 'react'
import {
    MdAdd,
    MdCheckCircle,
    MdDelete,
    MdEdit,
    MdFilterList,
    MdAssignmentLate,
    MdRefresh,
    MdSwapVert,
    MdTimer,
    MdWarning,
} from 'react-icons/md'
import { toast } from 'react-toastify'

type SectionTab = 'rules' | 'breaches'

const SLA_TYPE_ICONS: Record<SLAType, React.ReactNode> = {
    FIRST_RESPONSE: <MdTimer size={16} className="text-info" />,
    STAGE_TIME: <MdSwapVert size={16} className="text-warning" />,
    NO_ACTIVITY: <MdWarning size={16} className="text-error" />,
    TASK_OVERDUE: <MdAssignmentLate size={16} className="text-error" />,
}

const plural = (n: number, singular: string, pluralForm: string) => `${n} ${n === 1 ? singular : pluralForm}`

function formatTargetMinutes(minutes: number): string {
    if (minutes < 60) return plural(minutes, 'minuto', 'minutos')
    if (minutes < 1440) {
        const h = Math.floor(minutes / 60)
        const m = minutes % 60
        return m > 0 ? `${plural(h, 'hora', 'horas')} e ${m} min` : plural(h, 'hora', 'horas')
    }
    const d = Math.floor(minutes / 1440)
    const rem = minutes % 1440
    if (rem === 0) return plural(d, 'dia', 'dias')
    return `${plural(d, 'dia', 'dias')} e ${Math.floor(rem / 60)}h`
}

export default function SLAsPage() {
    const { user } = useAuth()
    const { canRead, canCreate, canUpdate, canDelete } = useResourcePermissions('sales', 'slas')

    const [section, setSection] = useState<SectionTab>('rules')
    const [slas, setSlas] = useState<SalesSLA[]>([])
    const [breaches, setBreaches] = useState<SLABreach[]>([])
    const [pipelines, setPipelines] = useState<SalesPipeline[]>([])
    const [loading, setLoading] = useState(true)

    // Create/Edit SLA
    const [showSLAModal, setShowSLAModal] = useState(false)
    const [slaForm, setSlaForm] = useState<CreateSLAData>({
        name: '',
        type: 'FIRST_RESPONSE',
        targetMinutes: 15,
    })
    const [editingSLAId, setEditingSLAId] = useState<number | null>(null)
    const [savingSLA, setSavingSLA] = useState(false)

    // Delete SLA
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [slaToDelete, setSlaToDelete] = useState<number | null>(null)
    const [deletingSLA, setDeletingSLA] = useState(false)

    // Breach filters
    const [breachFilters, setBreachFilters] = useState<SLABreachFilters>({})
    const [showBreachFilters, setShowBreachFilters] = useState(false)

    // Resolve breach
    const [showResolveModal, setShowResolveModal] = useState(false)
    const [breachToResolve, setBreachToResolve] = useState<number | null>(null)
    const [resolveAction, setResolveAction] = useState('')
    const [resolving, setResolving] = useState(false)

    useEffect(() => {
        loadPipelines()
    }, [])

    useEffect(() => {
        if (section === 'rules') loadSLAs()
        if (section === 'breaches') loadBreaches()
    }, [section])

    useEffect(() => {
        if (section === 'breaches') loadBreaches()
    }, [breachFilters])

    const loadSLAs = async () => {
        setLoading(true)
        try {
            const response = await getSLAs()
            if (response.success && response.result) {
                setSlas(response.result)
            }
        } catch {
            toast.error('Erro ao carregar SLAs')
        } finally {
            setLoading(false)
        }
    }

    const loadBreaches = async () => {
        setLoading(true)
        try {
            const response = await getSLABreaches(breachFilters)
            if (response.success && response.result) {
                setBreaches(response.result)
            }
        } catch {
            toast.error('Erro ao carregar violações')
        } finally {
            setLoading(false)
        }
    }

    const loadPipelines = async () => {
        try {
            const response = await getPipelines()
            if (response.success && response.result) setPipelines(response.result)
        } catch {
            // Silently fail
        }
    }

    const selectedPipelineStages = useMemo(() => {
        if (!slaForm.pipelineId) return []
        const p = pipelines.find(p => p.id === slaForm.pipelineId)
        return (p?.stages || []).sort((a, b) => a.sortOrder - b.sortOrder)
    }, [slaForm.pipelineId, pipelines])

    const handleSaveSLA = async () => {
        if (!slaForm.name || !slaForm.targetMinutes) {
            toast.error('Preencha os campos obrigatórios')
            return
        }
        if (slaForm.type === 'STAGE_TIME' && !slaForm.stageId) {
            toast.error('Escolha a etapa: o SLA mede o tempo parado nela')
            return
        }
        setSavingSLA(true)
        try {
            if (editingSLAId) {
                const response = await updateSLA(editingSLAId, slaForm as UpdateSLAData)
                if (response.success) {
                    toast.success('SLA atualizado')
                } else {
                    toast.error(response.error || 'Erro ao atualizar')
                    return
                }
            } else {
                const response = await createSLA(slaForm)
                if (response.success) {
                    toast.success('SLA criado com sucesso')
                } else {
                    toast.error(response.error || 'Erro ao criar')
                    return
                }
            }
            setShowSLAModal(false)
            setEditingSLAId(null)
            resetSLAForm()
            loadSLAs()
        } catch {
            toast.error('Erro ao salvar SLA')
        } finally {
            setSavingSLA(false)
        }
    }

    const handleDeleteSLA = async () => {
        if (!slaToDelete) return
        setDeletingSLA(true)
        try {
            const response = await deleteSLA(slaToDelete)
            if (response.success) {
                toast.success('SLA removido')
                loadSLAs()
            } else {
                toast.error(response.error || 'Erro ao remover')
            }
        } catch {
            toast.error('Erro ao remover SLA')
        } finally {
            setDeletingSLA(false)
            setShowDeleteModal(false)
            setSlaToDelete(null)
        }
    }

    const handleToggleActive = async (sla: SalesSLA) => {
        try {
            const response = await updateSLA(sla.id, { active: !sla.active })
            if (response.success) {
                toast.success(sla.active ? 'SLA desativado' : 'SLA ativado')
                loadSLAs()
            }
        } catch {
            toast.error('Erro ao alterar status')
        }
    }

    const handleResolveBreach = async () => {
        if (!breachToResolve || !resolveAction.trim()) {
            toast.error('Descreva a ação tomada')
            return
        }
        setResolving(true)
        try {
            const response = await resolveSLABreach(breachToResolve, resolveAction)
            if (response.success) {
                toast.success('Violação resolvida')
                setShowResolveModal(false)
                setBreachToResolve(null)
                setResolveAction('')
                loadBreaches()
            } else {
                toast.error(response.error || 'Erro ao resolver')
            }
        } catch {
            toast.error('Erro ao resolver violação')
        } finally {
            setResolving(false)
        }
    }

    const resetSLAForm = () => {
        setSlaForm({
            name: '',
            type: 'FIRST_RESPONSE',
            targetMinutes: 15,
        })
    }

    const openEditSLA = (sla: SalesSLA) => {
        setEditingSLAId(sla.id)
        setSlaForm({
            name: sla.name,
            description: sla.description || '',
            type: sla.type,
            targetMinutes: sla.targetMinutes,
            pipelineId: sla.pipelineId || undefined,
            stageId: sla.stageId || undefined,
        })
        setShowSLAModal(true)
    }

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">SLAs de Vendas</h1>
            </div>

            {/* Section Tabs */}
            <div role="tablist" className="tabs tabs-bordered">
                <button
                    role="tab"
                    className={`tab ${section === 'rules' ? 'tab-active' : ''}`}
                    onClick={() => setSection('rules')}
                >
                    Regras
                </button>
                <button
                    role="tab"
                    className={`tab ${section === 'breaches' ? 'tab-active' : ''}`}
                    onClick={() => setSection('breaches')}
                >
                    Violações
                </button>
            </div>

            {/* ==================== RULES SECTION ==================== */}
            {section === 'rules' && (
                <div className="space-y-4">
                    <div className="flex justify-end">
                        {canCreate && (
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                    setEditingSLAId(null)
                                    resetSLAForm()
                                    setShowSLAModal(true)
                                }}
                            >
                                <MdAdd size={16} /> Nova Regra
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <span className="loading loading-spinner loading-lg" />
                        </div>
                    ) : slas.length === 0 ? (
                        <div className="text-center py-16 opacity-50">
                            <MdTimer size={48} className="mx-auto mb-3" />
                            <p>Nenhuma regra de SLA configurada</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {slas.map(sla => (
                                <div key={sla.id} className={`card bg-base-100 shadow-sm ${!sla.active ? 'opacity-60' : ''}`}>
                                    <div className="card-body p-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-2">
                                                {SLA_TYPE_ICONS[sla.type]}
                                                <h3 className="font-semibold">{sla.name}</h3>
                                            </div>
                                            {canUpdate && (
                                                <input
                                                    type="checkbox"
                                                    className="toggle toggle-xs toggle-success"
                                                    checked={sla.active}
                                                    onChange={() => handleToggleActive(sla)}
                                                />
                                            )}
                                        </div>
                                        {sla.description && (
                                            <p className="text-sm opacity-60 mt-1">{sla.description}</p>
                                        )}
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            <span className="badge badge-sm badge-outline">
                                                {SLA_TYPE_LABELS[sla.type]}
                                            </span>
                                            <span className="badge badge-sm badge-secondary">
                                                {formatTargetMinutes(sla.targetMinutes)}
                                            </span>
                                        </div>
                                        {(sla.pipeline || sla.stage) && (
                                            <div className="text-xs opacity-50 mt-2">
                                                Escopo: {sla.pipeline?.name || 'Todos os funis'}
                                                {sla.stage ? ` > ${sla.stage.name}` : ''}
                                            </div>
                                        )}
                                        {sla._count && sla._count.breaches > 0 && (
                                            <div className="text-xs text-error mt-1">
                                                {plural(sla._count.breaches, 'violação', 'violações')}
                                            </div>
                                        )}
                                        <div className="card-actions justify-end mt-3">
                                            {canUpdate && (
                                                <button
                                                    className="btn btn-ghost btn-xs"
                                                    onClick={() => openEditSLA(sla)}
                                                >
                                                    <MdEdit size={14} /> Editar
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    className="btn btn-ghost btn-xs text-error"
                                                    onClick={() => {
                                                        setSlaToDelete(sla.id)
                                                        setShowDeleteModal(true)
                                                    }}
                                                >
                                                    <MdDelete size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ==================== BREACHES SECTION ==================== */}
            {section === 'breaches' && (
                <div className="space-y-4">
                    <div className="flex justify-between">
                        <button
                            className="btn btn-sm btn-outline"
                            onClick={() => setShowBreachFilters(!showBreachFilters)}
                        >
                            <MdFilterList size={16} /> Filtros
                        </button>
                        <button className="btn btn-sm btn-ghost" onClick={loadBreaches}>
                            <MdRefresh size={16} /> Atualizar
                        </button>
                    </div>

                    {showBreachFilters && (
                        <div className="card bg-base-100 shadow-sm">
                            <div className="card-body">
                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                                    <div className="form-control">
                                        <label className="label"><span className="label-text">SLA</span></label>
                                        <select
                                            className="select select-bordered select-sm"
                                            value={breachFilters.slaId || ''}
                                            onChange={e =>
                                                setBreachFilters({
                                                    ...breachFilters,
                                                    slaId: e.target.value ? parseInt(e.target.value) : undefined,
                                                })
                                            }
                                        >
                                            <option value="">Todos</option>
                                            {slas.map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-control">
                                        <label className="label"><span className="label-text">Status</span></label>
                                        <select
                                            className="select select-bordered select-sm"
                                            value={breachFilters.resolved === undefined ? '' : breachFilters.resolved ? 'true' : 'false'}
                                            onChange={e =>
                                                setBreachFilters({
                                                    ...breachFilters,
                                                    resolved: e.target.value === '' ? undefined : e.target.value === 'true',
                                                })
                                            }
                                        >
                                            <option value="">Todas</option>
                                            <option value="false">Não Resolvidas</option>
                                            <option value="true">Resolvidas</option>
                                        </select>
                                    </div>
                                    <div className="form-control">
                                        <label className="label"><span className="label-text">Data Início</span></label>
                                        <input
                                            type="date"
                                            className="input input-bordered input-sm"
                                            value={breachFilters.startDate || ''}
                                            onChange={e =>
                                                setBreachFilters({ ...breachFilters, startDate: e.target.value || undefined })
                                            }
                                        />
                                    </div>
                                    <div className="form-control">
                                        <label className="label"><span className="label-text">Data Fim</span></label>
                                        <input
                                            type="date"
                                            className="input input-bordered input-sm"
                                            value={breachFilters.endDate || ''}
                                            onChange={e =>
                                                setBreachFilters({ ...breachFilters, endDate: e.target.value || undefined })
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end mt-2">
                                    <button
                                        className="btn btn-ghost btn-xs"
                                        onClick={() => setBreachFilters({})}
                                    >
                                        Limpar Filtros
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <span className="loading loading-spinner loading-lg" />
                        </div>
                    ) : breaches.length === 0 ? (
                        <div className="text-center py-16 opacity-50">
                            <MdCheckCircle size={48} className="mx-auto mb-3" />
                            <p>Nenhuma violação encontrada</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>SLA</th>
                                        <th>Tipo</th>
                                        <th>Lead</th>
                                        <th>Oportunidade</th>
                                        <th>Violado há</th>
                                        <th>Status</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {breaches.map(breach => (
                                        <tr
                                            key={breach.id}
                                            className={!breach.resolvedAt ? 'bg-error/5' : ''}
                                        >
                                            <td className="font-medium">{breach.sla?.name || '-'}</td>
                                            <td>
                                                {breach.sla?.type ? (
                                                    <span className="badge badge-sm badge-outline">
                                                        {SLA_TYPE_LABELS[breach.sla.type]}
                                                    </span>
                                                ) : '-'}
                                            </td>
                                            <td>{breach.lead?.name || '-'}</td>
                                            <td>{breach.deal?.title || '-'}</td>
                                            <td className="text-sm">
                                                {formatDistanceToNow(new Date(breach.breachedAt), {
                                                    addSuffix: true,
                                                    locale: ptBR,
                                                })}
                                            </td>
                                            <td>
                                                {breach.resolvedAt ? (
                                                    <span className="badge badge-sm badge-success gap-1">
                                                        <MdCheckCircle size={10} /> Resolvida
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-sm badge-error gap-1">
                                                        <MdWarning size={10} /> Pendente
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {!breach.resolvedAt && canUpdate && (
                                                    <button
                                                        className="btn btn-sm btn-success btn-outline"
                                                        onClick={() => {
                                                            setBreachToResolve(breach.id)
                                                            setResolveAction('')
                                                            setShowResolveModal(true)
                                                        }}
                                                    >
                                                        Resolver
                                                    </button>
                                                )}
                                                {breach.resolvedAt && breach.actionTaken && (
                                                    <div className="tooltip" data-tip={breach.actionTaken}>
                                                        <span className="text-xs opacity-60 cursor-help underline">Ver ação</span>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ==================== Create/Edit SLA Modal ==================== */}
            {showSLAModal && (
                <dialog className="modal modal-open">
                    <div className="modal-box !max-w-lg">
                        <h3 className="font-bold text-lg mb-4">
                            {editingSLAId ? 'Editar Regra de SLA' : 'Nova Regra de SLA'}
                        </h3>
                        <div className="space-y-4">
                            <div className="form-control">
                                <label className="label"><span className="label-text">Nome *</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={slaForm.name}
                                    onChange={e => setSlaForm({ ...slaForm, name: e.target.value })}
                                    placeholder="Nome da regra"
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">Descrição</span></label>
                                <textarea
                                    className="textarea textarea-bordered  w-full"
                                    rows={2}
                                    value={slaForm.description || ''}
                                    onChange={e => setSlaForm({ ...slaForm, description: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">Tipo *</span></label>
                                <select
                                    className="select select-bordered  w-full"
                                    value={slaForm.type}
                                    onChange={e => setSlaForm({ ...slaForm, type: e.target.value as SLAType })}
                                >
                                    {Object.entries(SLA_TYPE_LABELS).map(([val, label]) => (
                                        <option key={val} value={val}>{label}</option>
                                    ))}
                                </select>
                                <p className="mt-2 text-xs leading-relaxed text-base-content/60">{SLA_TYPE_DESCRIPTIONS[slaForm.type]}</p>
                            </div>
                            <div className="form-control">
                                <label className="label" htmlFor="sla-target"><span className="label-text">Tempo limite (minutos) *</span></label>
                                <input
                                    id="sla-target"
                                    type="number"
                                    className="input input-bordered w-full"
                                    min={1}
                                    value={slaForm.targetMinutes}
                                    onChange={e =>
                                        setSlaForm({ ...slaForm, targetMinutes: parseInt(e.target.value) || 0 })
                                    }
                                />
                                {slaForm.targetMinutes > 0 && (
                                    <p className="mt-1 text-xs text-base-content/60">Equivale a {formatTargetMinutes(slaForm.targetMinutes)}.</p>
                                )}
                                <div className="flex gap-1 mt-2 flex-wrap">
                                    {[
                                        [15, '15min'],
                                        [30, '30min'],
                                        [60, '1h'],
                                        [240, '4h'],
                                        [480, '8h'],
                                        [1440, '24h'],
                                        [2880, '48h'],
                                        [10080, '7 dias'],
                                    ].map(([val, label]) => (
                                        <button
                                            key={val}
                                            type="button"
                                            className={`btn btn-xs ${slaForm.targetMinutes === val ? 'btn-secondary' : 'btn-outline'}`}
                                            onClick={() => setSlaForm({ ...slaForm, targetMinutes: val as number })}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {slaForm.type === 'STAGE_TIME' && (
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div className="form-control">
                                        <label className="label" htmlFor="sla-pipeline"><span className="label-text">Funil *</span></label>
                                        <select
                                            id="sla-pipeline"
                                            className="select select-bordered w-full"
                                            value={slaForm.pipelineId || ''}
                                            onChange={e =>
                                                setSlaForm({
                                                    ...slaForm,
                                                    pipelineId: e.target.value ? parseInt(e.target.value) : undefined,
                                                    stageId: undefined,
                                                })
                                            }
                                        >
                                            <option value="">Selecione o funil</option>
                                            {pipelines.map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-control">
                                        <label className="label" htmlFor="sla-stage"><span className="label-text">Etapa *</span></label>
                                        <select
                                            id="sla-stage"
                                            className="select select-bordered w-full"
                                            value={slaForm.stageId || ''}
                                            disabled={!slaForm.pipelineId}
                                            onChange={e =>
                                                setSlaForm({
                                                    ...slaForm,
                                                    stageId: e.target.value ? parseInt(e.target.value) : undefined,
                                                })
                                            }
                                        >
                                            <option value="">{slaForm.pipelineId ? 'Selecione a etapa' : 'Escolha o funil primeiro'}</option>
                                            {selectedPipelineStages.map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                            <div role="note" className="rounded-xl border border-base-300 bg-base-200/50 p-4 text-sm leading-relaxed">
                                <p className="font-medium">O SLA mede; quem age é o fluxo</p>
                                <p className="mt-1 text-xs text-base-content/70">
                                    Cada violação fica registrada e se resolve sozinha quando a situação muda. Para avisar alguém,
                                    escalar ou trocar o responsável, crie um fluxo em Automações e fluxos com o gatilho
                                    "SLA violado".
                                </p>
                            </div>
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => {
                                    setShowSLAModal(false)
                                    setEditingSLAId(null)
                                }}
                            >
                                Cancelar
                            </button>
                            <button className="btn btn-secondary" onClick={handleSaveSLA} disabled={savingSLA}>
                                {savingSLA && <span className="loading loading-spinner loading-sm" />}
                                {editingSLAId ? 'Salvar' : 'Criar'}
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => { setShowSLAModal(false); setEditingSLAId(null) }}>close</button>
                    </form>
                </dialog>
            )}

            {/* Resolve Breach Modal */}
            {showResolveModal && (
                <dialog className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-4">Resolver Violação</h3>
                        <div className="form-control">
                            <label className="label"><span className="label-text">Ação Tomada *</span></label>
                            <textarea
                                className="textarea textarea-bordered"
                                rows={4}
                                value={resolveAction}
                                onChange={e => setResolveAction(e.target.value)}
                                placeholder="Descreva a ação tomada para resolver esta violação..."
                            />
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => {
                                    setShowResolveModal(false)
                                    setBreachToResolve(null)
                                }}
                            >
                                Cancelar
                            </button>
                            <button className="btn btn-success" onClick={handleResolveBreach} disabled={resolving}>
                                {resolving && <span className="loading loading-spinner loading-sm" />}
                                Resolver
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => { setShowResolveModal(false); setBreachToResolve(null) }}>close</button>
                    </form>
                </dialog>
            )}

            {/* Delete SLA Confirmation */}
            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => { setShowDeleteModal(false); setSlaToDelete(null) }}
                onConfirm={handleDeleteSLA}
                title="Remover Regra de SLA"
                message="Tem certeza que deseja remover esta regra de SLA? As violações existentes serão mantidas."
                loading={deletingSLA}
                confirmButtonText="Remover"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
